import { gql, useApolloClient, useMutation, useQuery } from "@apollo/client";
import { Alert, Box, Button, Checkbox, FormControlLabel, MenuItem, Stack } from "@mui/material";
import { useCallback, useContext, useState } from "react";
import { PRODUCTS, PROPOSAL, SAVE_ITEM, SAVE_PRODUCT, SETTINGS } from "./api";
import UiButton from "components/base/UiButton";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";
import WorksheetColumnHeaders from "./WorksheetColumnHeaders";
import WorksheetEditor from "./WorksheetEditor";
import { ProposalFormsContext } from "modules/proposals/providers/ProposalFormsContextProvider";

function ProposalWorksheetEditor({
  formId,
  ...props
}: React.ComponentProps<typeof WorksheetEditor> & { formId: string }) {
  const { setProductDirtyStatus, setProductSubmitFunction } = useContext(ProposalFormsContext);
  const onDirtyChange = useCallback(
    (dirty: boolean) => setProductDirtyStatus(formId, dirty),
    [formId, setProductDirtyStatus]
  );
  const registerSave = useCallback(
    (save: () => Promise<{ status: boolean; data: null }>) =>
      setProductSubmitFunction(formId, save),
    [formId, setProductSubmitFunction]
  );
  return <WorksheetEditor {...props} onDirtyChange={onDirtyChange} registerSave={registerSave} />;
}
import { blankWorksheet, Worksheet, worksheetInput } from "./calculator";
const DELETE = gql`
  mutation DeleteWorksheetItem($id: ID!) {
    deleteProposalProduct(id: $id) {
      id
    }
  }
`;
const OPTIONAL = gql`
  mutation SetWorksheetOptional($id: ID!, $isOptional: Boolean!) {
    toggleProposalProductOptionality(id: $id, isOptional: $isOptional) {
      id
    }
  }
`;

type Props = { proposalId: string; stageId: string; readOnly: boolean; onArrange: () => void };
export default function WorksheetItems({ proposalId, stageId, readOnly, onArrange }: Props) {
  const client = useApolloClient();
  const { data, error } = useQuery(PROPOSAL, { variables: { id: proposalId } });
  const { data: settings } = useQuery(SETTINGS);
  const [save] = useMutation(SAVE_ITEM);
  const [saveProduct] = useMutation(SAVE_PRODUCT);
  const [remove] = useMutation(DELETE);
  const [optional] = useMutation(OPTIONAL);
  const [adding, setAdding] = useState<Worksheet | null>(null);
  const [failure, setFailure] = useState("");
  const [busy, setBusy] = useState(false);
  const items =
    data?.worksheetProposal.stages.find((stage: any) => stage.id === stageId)?.proposalProducts ??
    [];
  const refresh = async () => {
    await client.refetchQueries({ include: [PROPOSAL, "customGetProposal"] });
  };
  const store = async (input: Worksheet, id?: string, discount?: string) => {
    await save({ variables: { id, stageId, input: worksheetInput(input), discount } });
    await refresh();
  };
  const storeProduct =
    settings?.worksheetSettings.canManageProducts && settings?.worksheetSettings.enabled
      ? async (input: Worksheet, productTypeId: string) => {
          await saveProduct({
            variables: { input: worksheetInput(input), productTypeId },
            refetchQueries: [PRODUCTS],
          });
        }
      : undefined;
  const act = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setFailure("");
    try {
      await action();
      await refresh();
    } catch (e) {
      setFailure((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box sx={{ p: { xs: 1, md: 2 }, bgcolor: draftColors.pageBg, minWidth: 0 }}>
      {(error || failure) && <Alert severity="error">{error?.message || failure}</Alert>}
      {(items.length > 0 || adding) && <WorksheetColumnHeaders />}
      {items.map(
        (product: { id: string; worksheet: Worksheet; isOptional: boolean; discount: string }) => (
          <Box key={product.id}>
            <ProposalWorksheetEditor
              sharedColumnHeaders
              formId={product.id}
              optionalControl={
                !readOnly && (
                  <FormControlLabel
                    sx={{
                      m: 0,
                      "& .MuiCheckbox-root": { p: 0.5 },
                      "& .MuiFormControlLabel-label": {
                        fontSize: 12,
                        display: { xs: "inline", lg: "none" },
                      },
                    }}
                    label="Optional"
                    control={
                      <Checkbox
                        size="small"
                        inputProps={{ "aria-label": "Optional" }}
                        checked={product.isOptional}
                        disabled={busy}
                        onChange={(_, checked) =>
                          act(() =>
                            optional({ variables: { id: product.id, isOptional: checked } })
                          )
                        }
                      />
                    }
                  />
                )
              }
              actions={
                !readOnly
                  ? (close) => [
                      <MenuItem
                        key="duplicate"
                        disabled={busy}
                        onClick={() => {
                          act(() => store(product.worksheet, undefined, product.discount));
                          close();
                        }}
                      >
                        Duplicate line item
                      </MenuItem>,
                      <MenuItem
                        key="delete"
                        disabled={busy}
                        sx={{ color: draftColors.red }}
                        onClick={() => {
                          act(() => remove({ variables: { id: product.id } }));
                          close();
                        }}
                      >
                        Delete line item
                      </MenuItem>,
                    ]
                  : undefined
              }
              initial={product.worksheet}
              allowDiscount
              discountCents={product.discount}
              readOnly={readOnly}
              onSave={(input, discount) => store(input, product.id, discount)}
              onSaveProduct={readOnly ? undefined : storeProduct}
            />
          </Box>
        )
      )}
      {adding && !readOnly && (
        <>
          <ProposalWorksheetEditor
            sharedColumnHeaders
            formId={`worksheet-new-${stageId}`}
            initial={adding}
            allowDiscount
            key={JSON.stringify(adding)}
            onSave={async (input, discount) => {
              await store(input, undefined, discount);
              setAdding(null);
            }}
            onSaveProduct={storeProduct}
          />
          <Button onClick={() => setAdding(null)}>Cancel new item</Button>
        </>
      )}
      {!readOnly && !adding && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <UiButton
            uiVariant="primary-green"
            disabled={busy}
            onClick={() => setAdding(blankWorksheet(settings?.worksheetSettings.markup))}
          >
            + Add Blank Product
          </UiButton>
          {items.length > 1 && (
            <UiButton uiVariant="outlined-small" onClick={onArrange}>
              Arrange products
            </UiButton>
          )}
        </Stack>
      )}
    </Box>
  );
}
